import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { MissionHandoffProvider } from '@/store/missionHandoffContext';
import Navbar from '@/components/Navbar';
import GettingToKnowYouCard from '@/components/GettingToKnowYouCard';
import CleanBrokenVideosControl from '@/components/admin/CleanBrokenVideosControl';
import PaywallModal from '@/components/PaywallModal';
import GenerateContentModal from '@/components/GenerateContentModal';
import RunMyWeekModal from '@/components/RunMyWeekModal';
import CloneViralModal from '@/components/CloneViralModal';
import BuildOfferModal from '@/components/BuildOfferModal';
import EmailCampaignModal from '@/components/EmailCampaignModal';
import SalesPageModal from '@/components/SalesPageModal';
import MonetizationPlanModal from '@/components/MonetizationPlanModal';
import ViralIdeasModal from '@/components/ViralIdeasModal';
import FeedbackModal from '@/components/FeedbackModal';
import ImproveHookModal from '@/components/ImproveHookModal';
import ScoreContentModal from '@/components/ScoreContentModal';
import AnalyzeResultsModal from '@/components/AnalyzeResultsModal';
import ContentPackModal from '@/components/ContentPackModal';
import LinkInBioModal from '@/components/LinkInBioModal';
import WeeklyContentPlanner from '@/components/WeeklyContentPlanner';
import LeadMagnetBuilder from '@/components/LeadMagnetBuilder';
import SalesPageBuilder from '@/components/SalesPageBuilder';
import EbookBuilder from '@/components/EbookBuilder';
import EmailSequenceBuilder from '@/components/EmailSequenceBuilder';
import BusinessEssentialsBuilder from '@/components/BusinessEssentialsBuilder';
import BusinessCompletionState from '@/components/BusinessCompletionState';
import ProgressStats from '@/components/ProgressStats';
import StreakBadge from '@/components/StreakBadge';
import WeeklyReview from '@/components/WeeklyReview';
import ReferralCard from '@/components/ReferralCard';
import OnboardingChecklist from '@/components/OnboardingChecklist';
import AskPayGBot from '@/components/AskPayGBot';
import UsageMeters from '@/components/UsageMeters';
import AITwinSettingsModal from '@/components/AITwinSettingsModal';
import DailyMissionCard from '@/components/DailyMissionCard';
import ActiveBusinessPicker from '@/components/ActiveBusinessPicker';
import OnboardingFocusModal from '@/components/OnboardingFocusModal';
import OnboardingContinueButton from '@/components/OnboardingContinueButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import UpgradeButton from '@/components/UpgradeButton';
import PackagePreviewModal from '@/components/PackagePreviewModal';
import BusinessPlanReviewModal from '@/components/BusinessPlanReviewModal';
import FloatingHelpButton from '@/components/FloatingHelpButton';
import RunMyBusinessModal from '@/components/RunMyBusinessModal';
import MarketIntelligenceCard from '@/components/MarketIntelligenceCard';
import LiveIntelligenceCard from '@/components/LiveIntelligenceCard';
import WhatToDoNextCard from '@/components/WhatToDoNextCard';
import { 
  Lightning, Sparkle, ArrowRight, VideoCamera, PenNib, CheckCircle,
  TrendUp, CurrencyDollar, ChatCircle, Target, ArrowsClockwise, Confetti, Package, Eye, Rocket
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { getUser } from '@/utils/auth';
import api, { contentAPI, billingAPI } from '@/utils/api';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';
import { toast } from 'sonner';
import { checkUserMilestones } from '@/utils/achievements';
import { canGenerateBusinessPackage, getEffectiveTier } from '@/utils/trialAccess';
import useWorkflowProgress from '@/hooks/useWorkflowProgress';
import { getStepById, WORKFLOW as WORKFLOW_STEPS } from '@/config/businessWorkflow';
import BusinessCreditsPill from '@/components/BusinessCreditsPill';
import InsufficientCreditsModal from '@/components/InsufficientCreditsModal';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [user, setUserState] = useState(getUser());
  const [prevUser, setPrevUser] = useState(null);
  // Round 218A Phase 8D-2 — Insufficient Credits / Add Credits modal
  // owned at the Dashboard header so the ambient pill's "Add credits"
  // button can open it without any per-page plumbing.
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);

  // Round 218A — canonical workflow progress. All dashboard shortcut
  // navigation, START/DONE badges, "Next:" hints, and locked/unlocked
  // states MUST read from this hook. No component should re-implement
  // "what's next?" logic. See businessWorkflow.js.
  const {
    progress: workflowProgress,
    nextStep: workflowNextStep,
    isUnlocked: isWorkflowStepUnlocked,
    completionPercent: workflowCompletionPercent,
    refresh: refreshWorkflowProgress,
  } = useWorkflowProgress();
  const isStepDone = React.useCallback(
    (stepId) => {
      const step = getStepById(stepId);
      return !!(step && step.completedWhen(workflowProgress || {}));
    },
    [workflowProgress],
  );
  
  const [showPaywall, setShowPaywall] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showRunMyWeekModal, setShowRunMyWeekModal] = useState(false);
  const [showCloneViralModal, setShowCloneViralModal] = useState(false);
  const [showBuildOfferModal, setShowBuildOfferModal] = useState(false);
  const [showEmailCampaignModal, setShowEmailCampaignModal] = useState(false);
  const [showSalesPageModal, setShowSalesPageModal] = useState(false);
  const [showMonetizationPlanModal, setShowMonetizationPlanModal] = useState(false);
  const [showViralIdeasModal, setShowViralIdeasModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showImproveHookModal, setShowImproveHookModal] = useState(false);
  const [showScoreContentModal, setShowScoreContentModal] = useState(false);
  const [showWeeklyContentPlanner, setShowWeeklyContentPlanner] = useState(false);
  // Round 41 — admin diagnostic + recovery tools.
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [schedulerHealthLoading, setSchedulerHealthLoading] = useState(false);
  const [schedulerHealth, setSchedulerHealth] = useState(null);
  // Round 42 — Repair Calendar (gap-detector + self-healing)
  const [calendarScanLoading, setCalendarScanLoading] = useState(false);
  const [calendarScan, setCalendarScan] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [repairForce, setRepairForce] = useState(false);
  // Round 43 — Passive monitoring snapshot
  const [lastScanInfo, setLastScanInfo] = useState(null);
  const [alertNowLoading, setAlertNowLoading] = useState(false);
  const [showLeadMagnetBuilder, setShowLeadMagnetBuilder] = useState(false);
  const [showSalesPageBuilder, setShowSalesPageBuilder] = useState(false);
  const [showEbookBuilder, setShowEbookBuilder] = useState(false);
  const [showEmailSequenceBuilder, setShowEmailSequenceBuilder] = useState(false);
  const [showBusinessEssentialsBuilder, setShowBusinessEssentialsBuilder] = useState(false);
  const [showAnalyzeResultsModal, setShowAnalyzeResultsModal] = useState(false);
  const [chatContextForOffer, setChatContextForOffer] = useState(null);
  const [showContentPackModal, setShowContentPackModal] = useState(false);
  const [showLinkInBioModal, setShowLinkInBioModal] = useState(false);
  const [showAITwinModal, setShowAITwinModal] = useState(false);
  const [selectedBot, setSelectedBot] = useState('');
  const [todayMission, setTodayMission] = useState(null);
  const [recentContent, setRecentContent] = useState([]);
  const [loadingMission, setLoadingMission] = useState(true);
  const [initialTopic, setInitialTopic] = useState('');
  // Rich viral idea object carried forward into GenerateContentModal so the
  // user keeps the hook / angle / platform they picked instead of starting
  // from a blank topic field.
  const [selectedViralIdea, setSelectedViralIdea] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const [upgradeReason, setUpgradeReason] = useState('general');
  const [aiTwinRefresh, setAITwinRefresh] = useState(0);
  const [wizardChecked, setWizardChecked] = useState(false);
  const [showFocusModal, setShowFocusModal] = useState(false);
  const [showWinMoment, setShowWinMoment] = useState(false);
  const [winMessage, setWinMessage] = useState('');
  const [weeklyProgress, setWeeklyProgress] = useState(null);
  const [generatingPackage, setGeneratingPackage] = useState(false);
  const [showPackagePreview, setShowPackagePreview] = useState(false);
  const [showPlanReview, setShowPlanReview] = useState(false);
  const [businessPlanApproved, setBusinessPlanApproved] = useState(false);
  const [showRunMyBusinessModal, setShowRunMyBusinessModal] = useState(false);
  // Round 218D P0 (2026-02-07) — canonical builder-completion CTA.
  // Every onboarding builder that fires ``onSuccess`` triggers this
  // dialog. The dialog itself reads /api/foundation/next-action and
  // renders the resolver-driven CTA — no hardcoded transitions.
  const [continueDialogOpen, setContinueDialogOpen] = useState(false);
  const [continueDialogMeta, setContinueDialogMeta] = useState({ title: '', subtitle: null });
  // Round 107 (2026-02-14) — strategist → specialized-builder
  // handoff. When the customer clicks "Create <Asset>" on a
  // mission-linked strategist result page they land back on
  // /dashboard with `?open_builder=<action>&mid=<asset_id>&wctx=<b64>&strategist=<b64>`.
  // We store the decoded mission handoff here so any child modal
  // (LeadMagnetBuilder, EmailSequenceBuilder, etc.) can look it
  // up and prefill from it. On any successful builder completion
  // (`window.dispatchEvent(new CustomEvent('paygbot:builder-completed', { detail }))`)
  // we POST to /api/todays-mission-v2/{asset_id}/finalize — the
  // originating mission stays authoritative, no duplicate row.
  const [missionHandoff, setMissionHandoff] = useState(null);

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Your PayGBot plan is now active!');
      window.history.replaceState({}, '', '/dashboard');
      window.location.reload();
    }
    // PART 7: Check for video credit purchase success
    if (searchParams.get('video_credits_purchased') === 'true') {
      const bundle = searchParams.get('bundle');
      toast.success(`${bundle || ''} video credits added to your account!`.trim());
      window.history.replaceState({}, '', '/dashboard');
    }
    
    // Check if redirected from onboarding with openMoneyBot flag
    if (location.state?.openMoneyBot) {
      setShowBuildOfferModal(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, '', '/dashboard');
    }

    // Round 218C P0 (2026-02-07) — canonical resolver builder opener.
    // ``?openBuilder=<step_id>`` is emitted by the next-action
    // resolver's route field so each completion modal's CTA lands
    // the customer directly on the correct next builder. Kept
    // separate from ``?openPlan=1`` (Business Builder Bot) and
    // ``?open_builder=<action>`` (mission handoff) so no existing
    // deep-link contract changes.
    const openBuilderId = searchParams.get('openBuilder');
    if (openBuilderId) {
      const map = {
        offer:                'build-offer',
        sales_page:           'sales-page',
        lead_magnet:          'lead-magnet',
        emails:               'email-sequence',
        business_essentials:  'business-essentials',
        ebook:                null, // ebook opens through builder card below
        viral_ideas:          'viral-ideas',
        content:              'generate-content',
        growth:               'run-my-week',
        run_my_business:      null,
        run_my_week:          'run-my-week',
      };
      const action = map[openBuilderId];
      if (action) {
        setTimeout(() => { try { handleWhatsNextAction(action); } catch { /* noop */ } }, 30);
      } else if (openBuilderId === 'ebook') {
        setTimeout(() => setShowEbookBuilder(true), 30);
      } else if (openBuilderId === 'run_my_business') {
        setTimeout(() => setShowRunMyBusinessModal(true), 30);
      }
      // Round 218D P0 (2026-02-07) — do NOT strip the resolver's
      // ?openBuilder=<id> from the URL. Preserving it keeps the
      // canonical destination visible in the address bar (matches
      // the acceptance test) and is safe: the resolver is
      // forward-monotonic so on refresh it opens the SAME step
      // (already the correct next step) and never loops.
    }
    
    fetchUserData();
    fetchTodayMission();
    fetchRecentContent();
    fetchBillingInfo();
    fetchWeeklyProgress();
    checkBusinessPlanApproval(); // Check if plan is approved for Money Bot access
  }, [searchParams, location.state]);

  // Round 113 — Brand Brain gate. On first dashboard render after
  // Business Brain onboarding is complete, redirect the user to
  // /brand-brain-setup if their Brand Brain has not been finished.
  // The gate self-clears once `onboarding_completed=true` is stamped
  // on brand_brain, so subsequent dashboard visits are unaffected.
  //
  // Round 218B P0 (2026-02-07) — the gate MUST NOT fire before
  // Business Builder Bot onboarding is complete. Brand Brain is a
  // downstream step in the canonical orchestration (BBB plan → MI →
  // OI → Offer → Brand Brain), so racing it against ?openPlan=1 or
  // an incomplete BBB run hijacks the customer to /brand-brain-setup
  // and hides the primary onboarding UI. Skip the gate whenever:
  //   • ?openPlan=1 is in the URL (explicit request to reopen BBB), OR
  //   • the customer has not completed BBB yet (user.onboarding_completed=false)
  useEffect(() => {
    let cancelled = false;
    const wantOpenPlan =
      new URLSearchParams(location.search).get('openPlan') === '1';
    if (wantOpenPlan) return;
    // BBB not finished yet → let Business Builder Bot run first.
    if (user && user.onboarding_completed !== true) return;
    (async () => {
      try {
        const { brandBrainAPI } = await import('@/utils/api');
        const data = await brandBrainAPI.get();
        if (cancelled) return;
        const bb = data?.brand_brain;
        if (bb && bb.onboarding_completed === false) {
          navigate('/brand-brain-setup');
        }
      } catch (err) {
        // Non-fatal: if Brand Brain endpoint fails we do not block
        // the dashboard.
        console.warn('brand-brain gate skipped:', err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.onboarding_completed, location.search]);

  // Round 107 — strategist → specialized-builder handoff.
  // Parses ?open_builder=<action>&mid=<asset_id>&wctx=<b64>&strategist=<b64>
  // then (a) stores the decoded handoff in `missionHandoff` state
  // so modals can prefill from it, and (b) opens the correct
  // modal via the existing `handleWhatsNextAction()` dispatcher.
  useEffect(() => {
    const openBuilder = searchParams.get('open_builder');
    if (!openBuilder) return;
    const missionAssetId = searchParams.get('mid') || null;
    const missionAssetType = searchParams.get('mission_asset_type') || null;
    const rawWctx = searchParams.get('wctx') || '';
    const rawStrategist = searchParams.get('strategist') || '';
    const decode = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(decodeURIComponent(escape(window.atob(raw))));
      } catch (_) {
        return null;
      }
    };
    const wctx = decode(rawWctx) || {};
    const strategistOutputs = decode(rawStrategist) || null;
    setMissionHandoff({
      asset_id: missionAssetId,
      asset_type: missionAssetType || wctx.asset_type || null,
      mission_id: wctx.mission_id || missionAssetId,
      mission_title: wctx.mission_title || null,
      approved_edits: wctx.approved_edits || {},
      strategist_outputs: strategistOutputs,
      asset_payload: wctx.asset_payload_summary || wctx.approved_edits || {},
      business_id: wctx.business_id || null,
      business_context: wctx.business_context || {},
      niche: wctx.niche || null,
      target_audience: wctx.target_audience || null,
      preferred_platform: wctx.preferred_platform || null,
      publishing_instructions: wctx.publishing_instructions || null,
      brand_voice: wctx.brand_voice || null,
    });
    // Round 108 — pre-hydrate content-modal seeds so the customer
    // never sees the generic "What topic should I create content
    // about?" prompt when arriving from a mission. Applies only
    // when the builder we're about to open is the content builder.
    if (openBuilder === 'generate-content') {
      const at = wctx.approved_edits || {};
      const so = strategistOutputs || {};
      const topic =
        at.title
        || at.hook
        || (Array.isArray(so.hooks) && so.hooks.length ? so.hooks[0] : null)
        || at.caption
        || wctx.mission_title
        || '';
      if (topic) setInitialTopic(topic);
      if (at.hook || at.caption || so.hooks) {
        setSelectedViralIdea({
          topic: topic,
          title: at.title || wctx.mission_title || topic,
          hook: at.hook || (Array.isArray(so.hooks) && so.hooks[0]) || null,
          angle: at.caption || null,
          idea: topic,
        });
      }
    }
    // Clean the URL so a refresh doesn't reopen the modal.
    try {
      window.history.replaceState({}, '', '/dashboard');
    } catch (_) { /* noop */ }
    // Delay the dispatch to next tick so the state is set before
    // the modal reads its `initialMissionContext` via context/prop.
    setTimeout(() => {
      try { handleWhatsNextAction(openBuilder); } catch (_) { /* noop */ }
    }, 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Round 107 — global listener: when any specialized builder
  // reports a successful creation via
  // `window.dispatchEvent(new CustomEvent('paygbot:builder-completed', { detail: { meta } }))`,
  // POST /api/todays-mission-v2/{asset_id}/finalize with the
  // builder's output as the finalized meta. The mission is now
  // authoritative and marked ready.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = async (e) => {
      const handoff = missionHandoff;
      if (!handoff || !handoff.asset_id) return;
      const detail = (e && e.detail) || {};
      const metaPatch = detail.meta || detail.metaPatch || {};
      try {
        await api.post(
          `/todays-mission-v2/${handoff.asset_id}/finalize`,
          {
            meta: metaPatch,
            title: detail.title || handoff.mission_title || undefined,
            summary: detail.summary || undefined,
          },
        );
        toast.success('Saved to Today\'s Mission.');
        setMissionHandoff(null);
        // Refresh mission so the ExecutionPanel picks up finalized meta.
        try { fetchTodayMission(); } catch (_) { /* noop */ }
        // Dispatch task-completed so DailyMissionCard also refreshes.
        try {
          window.dispatchEvent(new CustomEvent('paygbot:task-completed', {
            detail: { asset_id: handoff.asset_id, source: 'builder' },
          }));
        } catch (_) { /* noop */ }
      } catch (err) {
        console.warn('[missionHandoff] finalize failed:', err);
      }
    };
    window.addEventListener('paygbot:builder-completed', handler);
    return () => window.removeEventListener('paygbot:builder-completed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionHandoff]);

  useEffect(() => {
    // Round 73 — The post-Stripe onboarding lives on the dashboard
    // itself. When a paid non-internal user lands on /dashboard with
    // onboarding_completed=false, we auto-open the EXACT modal the
    // dashboard's "Business Builder Bot → Build My Plan" CTA opens
    // (MonetizationPlanModal — header "Let's Plan Your Income").
    // No alternate route, no alternate form, no /bots/business-builder
    // detour. The legacy 4-step wizard was removed entirely in
    // Round 218A P0 (2026-02-07).
    if (!wizardChecked && user && user.id) {
      // ?openPlan=1 — set by the legacy /onboarding route redirect.
      // Also opens the modal regardless of onboarding state so
      // direct deep-links work.
      const wantOpenPlan = new URLSearchParams(location.search).get('openPlan') === '1';
      if (wantOpenPlan) {
        setWizardChecked(true);
        setShowMonetizationPlanModal(true);
        return;
      }
      if (user.onboarding_completed || user.is_internal) {
        setWizardChecked(true);
        return;
      }
      if (location.pathname === '/dashboard') {
        setWizardChecked(true);
        setShowMonetizationPlanModal(true);
        return;
      }
      setWizardChecked(true);
    }
  }, [user, wizardChecked, location.pathname, location.search]);

  const fetchBillingInfo = async () => {
    try {
      const response = await billingAPI.getInfo();
      setBillingInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch billing info:', error);
    }
  };

  const handleUpgradeClick = (reason = 'general') => {
    setUpgradeReason(reason);
    /* Phase G — no upgrade modal on V2 */
  };

  const handleFocusComplete = async () => {
    setShowFocusModal(false);
    localStorage.setItem('focus_modal_dismissed', 'true');
    await fetchUserData();
    toast.success('Great choice! Let\'s get started.');
  };

  const showWin = (message) => {
    setWinMessage(message);
    setShowWinMoment(true);
    setTimeout(() => setShowWinMoment(false), 3000);
  };

  const fetchUserData = async () => {
    try {
      const response = await contentAPI.getMe();
      const userData = response.data;
      setPrevUser(user);
      setUserState(userData);
      checkUserMilestones(userData, user);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Round 218B P0 (2026-02-07) — the legacy OnboardingFocusModal
      // ("What kind of business are you building?") has been retired.
      // Business Builder Bot (MonetizationPlanModal) is the ONE AND
      // ONLY first-time onboarding UI. Never trigger the focus modal
      // for anyone.
      // (Component still mounted below with isOpen=false so any deep
      //  imports do not crash; scheduled for full deletion.)
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  };

  const fetchTodayMission = async () => {
    // Phase 2 — prefer the asset-driven v2 endpoint. If it has a
    // mission with a real asset, surface it. Otherwise fall through
    // to the legacy topic-suggestion endpoint so empty-asset accounts
    // still see something useful in the card. Single try/catch so
    // the loading flag is reliably cleared and we don't introduce a
    // second async branch.
    try {
      let usedV2 = false;
      try {
        const v2 = await contentAPI.getTodayMissionV2();
        const m = v2?.data?.mission;
        if (v2?.data?.has_mission && m && m.asset_id) {
          setTodayMission({
            // Map v2 shape onto the existing card contract so we
            // don't need any UI redesign — the card reads `topic`
            // and shows it under the "Today's Topic" pill. We
            // surface the v2 action label (e.g. "Post Content #3 —
            // Scale Without Limits") so the customer sees execution
            // language, not "Create content".
            topic: m.action || m.title,
            description: m.summary || '',
            // v2 markers — let the card render the execution variant.
            v2: true,
            asset_id: m.asset_id,
            asset_type: m.asset_type,
            slot_index: m.slot_index,
            asset_title: m.title,
            asset_status: m.status,
          });
          usedV2 = true;
        }
      } catch (v2Err) {
        // v2 unavailable — fall through to legacy below.
        console.warn('todays-mission-v2 unavailable, falling back:', v2Err);
      }
      if (!usedV2) {
        const response = await contentAPI.getTodayMission();
        setTodayMission(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch mission:', error);
    } finally {
      setLoadingMission(false);
    }
  };

  const fetchRecentContent = async () => {
    try {
      const response = await contentAPI.getRecent();
      setRecentContent(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch recent content:', error);
    }
  };

  // Fetch persistent weekly progress on dashboard load
  const fetchWeeklyProgress = async () => {
    try {
      const response = await api.get('/weekly-progress');
      if (response.data.success) {
        setWeeklyProgress(response.data);
        
        // Update user state with persistent streak data
        if (response.data.streak) {
          setUserState(prev => ({
            ...prev,
            daily_login_streak: response.data.streak.current,
            posting_streak: response.data.streak.current,
            longest_posting_streak: response.data.streak.longest
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch weekly progress:', error);
    }
  };

  // Check if business plan is approved - needed for Money Bot access
  const checkBusinessPlanApproval = async () => {
    try {
      const response = await api.get('/business-brain/business-builder/review');
      if (response.data.has_plan) {
        setBusinessPlanApproved(response.data.is_approved || false);
      }
    } catch (error) {
      console.error('Failed to check business plan approval:', error);
    }
  };

  // Round 218A (revised) — Money Bot access is NEVER blocked from the
  // Dashboard. The Dashboard is the customer's workspace, not a wizard.
  // If a prerequisite step (e.g., Brand Builder) is missing, we show a
  // soft recommendation toast letting the customer either jump to the
  // missing step or continue anyway. The workflow engine is guidance
  // only — the customer owns their business and can revisit, edit,
  // or regenerate any asset whenever they choose.
  const handleMoneyBotAccess = (actionFn, stepId = null) => {
    // Legacy plan-approval gate is preserved (separate from the
    // workflow engine): first-time customers still need to approve
    // their auto-generated business plan.
    if (!businessPlanApproved) {
      setShowPlanReview(true);
      return;
    }
    // Canonical workflow: SOFT recommendation only. If the requested
    // customer-facing step is missing prerequisites (brand builder is
    // the main one today), surface a toast with two paths — go finish
    // the prerequisite OR proceed anyway. Never block.
    if (stepId && !isWorkflowStepUnlocked(stepId)) {
      toast.info(
        'We recommend completing Brand Builder first so your logo, colors, fonts, and brand voice are automatically applied.',
        {
          action: {
            label: 'Go to Brand Builder',
            onClick: () => navigate('/brand-brain-setup'),
          },
          cancel: {
            label: 'Continue anyway',
            onClick: () => actionFn(),
          },
          duration: 8000,
        },
      );
      return;
    }
    actionFn();
  };

  // Called when plan is approved in the review modal
  const handlePlanApproved = () => {
    setBusinessPlanApproved(true);
    setShowPlanReview(false);
    toast.success('Business plan approved! You can now access Money Bot.');
  };

  const handleGenerateClick = (topic = '') => {
    setInitialTopic(topic || todayMission?.topic || '');
    setShowGenerateModal(true);
  };

  /**
   * Central router invoked by every Money Bot modal's NextStepGuide.
   * Opens the requested builder next so the user stays inside the build loop.
   */
  const pickMoneyBotStep = (stepKey) => {    switch (stepKey) {
      case 'essentials':  setShowBusinessEssentialsBuilder(true); break;
      case 'offer':       setShowBuildOfferModal(true); break;
      case 'lead_magnet': setShowLeadMagnetBuilder(true); break;
      case 'sales_page':  setShowSalesPageBuilder(true); break;
      case 'ebook':       setShowEbookBuilder(true); break;
      case 'email':       setShowEmailSequenceBuilder(true); break;
      case 'viral_ideas': setShowViralIdeasModal(true); break;
      case 'content':     setShowGenerateModal(true); break;
      case 'growth':      setShowRunMyWeekModal(true); break;
      case 'run_my_business': setShowRunMyBusinessModal(true); break;
      default: break;
    }
  };

  // Round 107 — fire the builder-completed event so the global
  // listener (installed near line 195) can POST /finalize on the
  // originating mission asset. No-op when no mission handoff is
  // active — direct builder opens do NOT auto-finalize anything.
  const notifyBuilderCompleted = React.useCallback((data) => {
    // Round 218A — bump workflow progress so DONE badges and
    // completion percentage refresh right after a builder finishes.
    try { refreshWorkflowProgress?.(); } catch (_) { /* noop */ }
    if (missionHandoff && missionHandoff.asset_id) {
      try {
        window.dispatchEvent(new CustomEvent('paygbot:builder-completed', {
          detail: data || {},
        }));
      } catch (_) { /* noop */ }
    }
    // Round 218D P0 (2026-02-07) — every onboarding builder's
    // completion routes via the canonical resolver. We open the
    // shared continue dialog which asks GET /api/foundation/next-action
    // and renders the resolver's next_cta_label + next_route. Any
    // builder that fires ``onSuccess`` participates automatically —
    // no per-builder wiring required.
    setContinueDialogMeta({
      title: data?.meta?.title || data?.title || 'Saved to your business.',
      subtitle: data?.meta?.subtitle || data?.subtitle || null,
    });
    setContinueDialogOpen(true);
  }, [missionHandoff, refreshWorkflowProgress]);

  // Global bridge so deep UI (modals inside modals) can open any tool by
  // dispatching a `paygbot:open-tool` CustomEvent. Used by the Run My
  // Business "Finish Business Setup" CTA when memory is incomplete.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e) => {
      const tool = e?.detail?.tool;
      if (tool) pickMoneyBotStep(tool);
    };
    window.addEventListener('paygbot:open-tool', handler);
    return () => window.removeEventListener('paygbot:open-tool', handler);
  }, []);

  // Round 43 — Admin-only: load passive-monitoring snapshot for the
  // "Last successful scan" badge inside Internal Tools.
  const refreshLastScanInfo = React.useCallback(async () => {
    try {
      const res = await api.get('/admin/calendar/last-scan');
      setLastScanInfo(res.data);
    } catch (_e) {
      /* admin-only endpoint — silently ignored for non-admin users */
    }
  }, []);

  useEffect(() => {
    if (user?.is_internal || user?.plan_tier === 'internal' || user?.simulation_active) {
      refreshLastScanInfo();
    }
  }, [user?.is_internal, user?.plan_tier, user?.simulation_active, refreshLastScanInfo]);

  const handleWhatsNextAction = (action) => {
    switch (action) {
      case 'run-my-week':
      case 'weekly_plan':
        setShowRunMyWeekModal(true);
        break;
      case 'clone-viral':
        setShowCloneViralModal(true);
        break;
      case 'build-offer':
        setShowBuildOfferModal(true);
        break;
      case 'sales-page':
        setShowSalesPageBuilder(true);
        break;
      case 'lead-magnet':
      case 'lead_magnet':
        setShowLeadMagnetBuilder(true);
        break;
      case 'email-sequence':
      case 'email-campaign':
      case 'email_campaign':
        setShowEmailSequenceBuilder(true);
        break;
      case 'essentials':
      case 'business-essentials':
      case 'business_essentials':
        setShowBusinessEssentialsBuilder(true);
        break;
      case 'monetization-plan':
      case 'monetization_plan':
        setShowMonetizationPlanModal(true);
        break;
      case 'generate-content':
      case 'create_content':
      case 'create_more_content':
        setShowGenerateModal(true);
        break;
      case 'viral-ideas':
        setShowViralIdeasModal(true);
        break;
      case 'profile':
      case 'complete_profile':
        // Round 71 — single canonical Business Builder Bot flow.
        navigate('/bots/business-builder');
        break;
      case 'improve-hook':
        setShowImproveHookModal(true);
        break;
      case 'score-content':
        setShowScoreContentModal(true);
        break;
      case 'content-pack':
        setShowContentPackModal(true);
        break;
      case 'link-in-bio':
        setShowLinkInBioModal(true);
        break;
      default:
        break;
    }
  };

  /**
   * Called by ViralIdeasModal when the user picks an idea. `payload` may
   * be either a plain string (legacy) or the full idea object
   * `{ topic, hook, angle, platform, format, ... }`. We persist the full
   * object so GenerateContentModal can show the "Creating from selected
   * viral idea" banner and Create Content stays aligned with the hook
   * the user just chose.
   */
  const handleViralIdeaSelect = (payload) => {
    let idea = null;
    let topic = '';
    if (typeof payload === 'string') {
      topic = payload;
      idea = { topic };
    } else if (payload && typeof payload === 'object') {
      idea = payload;
      topic = payload.topic || payload.title || payload.idea || '';
    }
    setSelectedViralIdea(idea);
    setInitialTopic(topic);
    setShowViralIdeasModal(false);
    setShowGenerateModal(true);
  };

  // Same idea object, but routed into Content Pack instead of Generate Content.
  // Reuses the existing selectedIdea plumbing on ContentPackModal so memory
  // still wins for blank fields.
  const handleViralIdeaSelectForPack = (payload) => {
    let idea = null;
    let topic = '';
    if (typeof payload === 'string') {
      topic = payload;
      idea = { topic };
    } else if (payload && typeof payload === 'object') {
      idea = payload;
      topic = payload.topic || payload.title || payload.idea || '';
    }
    setSelectedViralIdea(idea);
    setInitialTopic(topic);
    setShowViralIdeasModal(false);
    setShowContentPackModal(true);
  };

  // Handle business package generation (Pro users only)
  // Package state
  const [showPackageDownload, setShowPackageDownload] = useState(false);
  const [emailingPackage, setEmailingPackage] = useState(false);
  const [packageEmail, setPackageEmail] = useState('');
  const [packageFileCount, setPackageFileCount] = useState(0);
  const [packageZipData, setPackageZipData] = useState(null);

  const handleGenerateBusinessPackage = async () => {
    if (!canGenerateBusinessPackage(user)) {
      setUpgradeReason('pro');
      /* Phase G — no upgrade modal on V2 */
      return;
    }
    
    setGeneratingPackage(true);
    try {
      const response = await api.post('/business-package/generate');
      
      if (response.data.success) {
        const count = response.data.file_count || 0;
        setPackageFileCount(count);
        setPackageZipData(response.data.zip_data || null);
        setPackageEmail(user?.email || '');
        setShowPackageDownload(true);
        toast.success(`Business package created with ${count} files!`);
      } else {
        toast.error(response.data.error || 'Failed to generate package');
      }
    } catch (error) {
      console.error('Failed to generate business package:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate business package');
    } finally {
      setGeneratingPackage(false);
    }
  };

  // Download package — platform-aware
  const handleDownloadPackage = async () => {
    /* Phase G — every V2 customer can export */
    if (!packageZipData) {
      toast.error('Package data not available. Try generating again.');
      return;
    }
    try {
      // Decode base64 to bytes
      const byteCharacters = atob(packageZipData);
      const byteNumbers = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([byteNumbers], { type: 'application/zip' });
      const file = new File([blob], 'BusinessPackage.zip', { type: 'application/zip' });

      // Method 1: Web Share API (works on iOS native + real Safari 15+)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'PayGBot Business Package',
        });
        toast.success('Package saved!');
        return;
      }

      // Detect if we're in a WebView (not real Safari)
      const ua = navigator.userAgent;
      const isIOSDevice = /iPhone|iPad|iPod/.test(ua);
      const isRealSafari = isIOSDevice && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
      const isWebView = isIOSDevice && !isRealSafari;

      if (isWebView) {
        // WebView cannot handle blob downloads — show helpful message
        toast.error('Downloads require the deployed app. Use "Email" below or "View in My Creations".');
        return;
      }

      // Method 2: iOS-Safari-safe blob download
      // (opens in new tab on iOS Safari so user can Share → Save to Files;
      //  classic anchor click on desktop)
      const res = await safeDownloadBlob(blob, 'BusinessPackage.zip');
      if (res.ios) {
        toast.success("Package opened — tap Share → Save to Files.", { duration: 6000 });
      } else {
        toast.success('Download started!');
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // User cancelled share sheet
      console.error('Download error:', err);
      toast.error('Download failed. Try email delivery instead.');
    }
  };

  const handleEmailPackage = async () => {
    if (!packageEmail || !packageEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setEmailingPackage(true);
    try {
      const response = await api.post('/business-package/email', { email: packageEmail });
      if (response.data.success) {
        toast.success(response.data.message);
        setShowPackageDownload(false);
        navigate('/my-creations');
      } else {
        toast.error('Failed to send email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to email package');
    } finally {
      setEmailingPackage(false);
    }
  };

  // Round 218A followup (2026-02-03) — Root cause of "Step 4: Create
  // your lead magnet" appearing for customers who already own one or
  // more lead magnets:
  //
  //   The previous getCurrentStage() logic read `user.lead_magnets_
  //   created_count`, `user.sales_pages_created_count`, etc., from
  //   the auth payload. These counters are onboarding-era fields that
  //   remain `None` for accounts whose assets were created outside
  //   the counter-incrementing path (dogfooding, seed scripts, prior
  //   bugs). The Founder account demonstrates the exact bug:
  //       user.lead_magnets_created_count == None
  //       my_creations lead_magnet count  == 1  (actual)
  //
  //   The previous P0 fix wired the Business Builder / Money Bot
  //   shortcut cards through `useWorkflowProgress`, but this
  //   "Today's Focus" hero card was a SEPARATE, older code path and
  //   was overlooked. That is why it did not resolve the case.
  //
  // Today's Focus is now driven from the same canonical workflow.
  // The workflow's own progress projection sources every completion
  // signal from actual assets (`/my-creations`) plus Brand Brain,
  // Market Intelligence, and Opportunity Intelligence — the true
  // Business Profile — so any account with real assets self-heals
  // without database edits. No hardcoded founder exceptions.
  const _stepFocusMap = {
    business_plan: {
      botLabel: 'Business Builder Bot',
      title: 'Build your foundation',
      description: "Create your business plan so you know exactly what you're building.",
      cta: 'Build My Plan',
      action: () => setShowMonetizationPlanModal(true),
      icon: Target,
      color: 'from-blue-500 to-cyan-500',
    },
    market_intelligence: {
      botLabel: 'Business Builder Bot',
      title: 'Refresh Market Intelligence',
      description: 'Understand your niche, competitors, and audience so every asset lands.',
      cta: 'View Market Intelligence',
      action: () => {
        const el = document.querySelector('[data-testid="market-intelligence-card"], [data-testid="market-intelligence-card-empty"], [data-testid="market-intelligence-card-loading"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      icon: Target,
      color: 'from-blue-500 to-cyan-500',
    },
    opportunity_intelligence: {
      botLabel: 'Business Builder Bot',
      title: 'Refresh Opportunity Intelligence',
      description: 'See which opportunities inside your market are worth chasing this week.',
      cta: 'View Opportunities',
      action: () => {
        try { api.post('/opportunity-intelligence/refresh').catch(() => {}); } catch (_) { /* silent */ }
        const el = document.querySelector('[data-testid="market-intelligence-card"], [data-testid="market-intelligence-card-empty"], [data-testid="market-intelligence-card-loading"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      icon: Target,
      color: 'from-blue-500 to-cyan-500',
    },
    offer: {
      // Round 218A Phase 8D-3D — the Offer belongs to Business Builder,
      // NEVER to Money Bot. The Offer is created ONCE and saved to
      // Business Brain permanently.
      botLabel: 'Business Builder Bot',
      title: 'Finalize your Offer',
      description: 'The one offer at the center of your business — saved to Business Brain and used by every downstream asset.',
      cta: 'Open Offer Builder',
      action: () => setShowBuildOfferModal(true),
      icon: CurrencyDollar,
      color: 'from-blue-500 to-cyan-500',
    },
    brand_builder: {
      botLabel: 'Business Builder Bot',
      title: 'Complete your Brand Builder',
      description: 'Lock in your logo, colors, fonts, and voice so every downstream asset stays on-brand.',
      cta: 'Open Brand Builder',
      action: () => navigate('/brand-brain-setup'),
      icon: Target,
      color: 'from-teal-500 to-emerald-500',
    },
    sales_page: {
      botLabel: 'Money Bot',
      title: 'Create your sales page',
      description: 'Build the page that turns visitors into paying customers.',
      cta: 'Create Sales Page',
      action: () => setShowSalesPageBuilder(true),
      icon: CurrencyDollar,
      color: 'from-emerald-500 to-green-500',
    },
    lead_magnet: {
      botLabel: 'Money Bot',
      title: 'Create your lead magnet',
      description: 'Create a free asset that attracts your ideal audience.',
      cta: 'Create Lead Magnet',
      action: () => setShowLeadMagnetBuilder(true),
      icon: CurrencyDollar,
      color: 'from-emerald-500 to-green-500',
    },
    emails: {
      botLabel: 'Money Bot',
      title: 'Create email sequence',
      description: 'Build emails that nurture leads into paying customers.',
      cta: 'Create Emails',
      action: () => setShowEmailSequenceBuilder(true),
      icon: CurrencyDollar,
      color: 'from-emerald-500 to-green-500',
    },
    essentials: {
      botLabel: 'Money Bot',
      title: 'Add your business essentials',
      description: 'Terms, refund policy, and everything customers expect from a legit business.',
      cta: 'Open Essentials',
      action: () => setShowBusinessEssentialsBuilder(true),
      icon: CurrencyDollar,
      color: 'from-emerald-500 to-green-500',
    },
    ebook: {
      botLabel: 'Money Bot',
      title: 'Publish your first ebook',
      description: 'Turn your knowledge into a premium ebook customers can buy or download.',
      cta: 'Open Ebook Builder',
      action: () => setShowEbookBuilder(true),
      icon: CurrencyDollar,
      color: 'from-emerald-500 to-green-500',
    },
    content: {
      botLabel: 'Content Bot',
      title: 'Create your first content',
      description: 'Now that you have an offer, create content to promote it.',
      cta: 'Generate My Content',
      action: () => setShowGenerateModal(true),
      icon: PenNib,
      color: 'from-purple-500 to-pink-500',
    },
    growth: {
      botLabel: 'Growth Bot',
      title: 'Plan your week',
      description: 'Keep the momentum going with a weekly content plan.',
      cta: 'Plan My Week',
      action: () => setShowRunMyWeekModal(true),
      icon: TrendUp,
      color: 'from-amber-500 to-orange-500',
    },
  };

  // Todays Focus is derived directly from the canonical workflow.
  // If the workflow is fully complete → default to the growth loop.
  const getTodaysFocus = () => {
    // A first-run user still on onboarding — keep the historical
    // "Build your foundation" copy so it matches the wizard.
    if (!user?.onboarding_completed) {
      const bp = _stepFocusMap.business_plan;
      return { ...bp, nextBot: 'Next: Money Bot' };
    }
    const stepId = workflowNextStep?.id || 'growth';
    const focus = _stepFocusMap[stepId] || _stepFocusMap.growth;
    // Compute the "next after this" hint from the workflow's own
    // ordering so we don't hardcode it here either.
    const currentIdx = WORKFLOW_STEPS.findIndex((s) => s.id === stepId);
    const upcoming = currentIdx >= 0 ? WORKFLOW_STEPS[currentIdx + 1] : null;
    const nextBot = upcoming
      ? `Next: ${upcoming.label}`
      : 'Repeat the loop';
    return { ...focus, nextBot };
  };

  const todaysFocus = getTodaysFocus();
  const FocusIcon = todaysFocus.icon;

  // System Loop stage indicator (Build → Monetize → Create → Grow).
  // Derived from the canonical workflow's next step so it stays in
  // sync with Today's Focus. No user.*_count reads.
  const _stepStageMap = {
    business_plan: 'build',
    market_intelligence: 'build',
    opportunity_intelligence: 'build',
    brand_builder: 'build',
    offer: 'monetize',
    sales_page: 'monetize',
    lead_magnet: 'monetize',
    emails: 'monetize',
    essentials: 'monetize',
    ebook: 'monetize',
    content: 'create',
    growth: 'grow',
  };
  const currentStage = !user?.onboarding_completed
    ? 'build'
    : (_stepStageMap[workflowNextStep?.id] || 'grow');

  // 4 Bot System — Round 218A: the Business Builder Bot card is the
  // master navigation surface. Every shortcut derives its DONE state,
  // "Next:" label, and unlock rules from useWorkflowProgress (see
  // businessWorkflow.js). No hardcoded ordering here anymore.
  const bbNextHint = workflowNextStep
    ? `Next: ${workflowNextStep.label}`
    : 'Everything set — keep growing';
  const bbDescription = workflowNextStep
    ? `${workflowCompletionPercent}% complete — next: ${workflowNextStep.label}`
    : 'Business setup complete — return anytime to update';
  const brandDone = isStepDone('brand_builder');
  const moneyBotDescription = brandDone
    ? 'Turn your offer into money-making assets'
    : 'Recommended: finish Brand Builder first';

  const botSystem = [
    {
      id: 'business-builder',
      name: 'Business Builder Bot',
      description: bbDescription,
      icon: Target,
      color: 'from-blue-500 to-cyan-500',
      borderColor: 'border-blue-500/30',
      bgColor: 'bg-blue-500/10',
      // Round 216 (2026-08-02) — Business Builder is no longer just
      // onboarding. It is the permanent home for updating the business
      // (add a service, change pricing, refresh strategy, update
      // brand). Round 218A — each shortcut is tied to a canonical
      // workflow step id so DONE badges, START highlighting, and the
      // "Next:" hint all read from useWorkflowProgress.
      primaryAction: () => setShowMonetizationPlanModal(true),
      primaryCta: 'Business Plan',
      primaryStepId: 'business_plan',
      secondaryAction: () => {
        // Market Intelligence lives on this same dashboard — scroll
        // the customer directly to the card. No new page required.
        const el = document.querySelector('[data-testid="market-intelligence-card"], [data-testid="market-intelligence-card-empty"], [data-testid="market-intelligence-card-loading"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      secondaryCta: 'Market Intelligence',
      secondaryStepId: 'market_intelligence',
      tertiaryAction: async () => {
        // Opportunity Intelligence — same dashboard scroll pattern.
        // Kick off a refresh so a fresh customer sees data on arrival.
        try {
          api.post('/opportunity-intelligence/refresh').catch(() => {});
        } catch (_) { /* silent */ }
        const el = document.querySelector('[data-testid="market-intelligence-card"], [data-testid="market-intelligence-card-empty"], [data-testid="market-intelligence-card-loading"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        toast.info('Opportunity Intelligence updating in the background — check the strategy card in a moment.');
      },
      tertiaryCta: 'Opportunity Intelligence',
      tertiaryStepId: 'opportunity_intelligence',
      quaternaryAction: () => setShowBuildOfferModal(true),
      quaternaryCta: 'Offer Builder',
      quaternaryStepId: 'offer',
      quinaryAction: () => navigate('/brand-brain-setup'),
      quinaryCta: 'Brand Builder',
      quinaryStepId: 'brand_builder',
      nextHint: bbNextHint,
      isStart: !workflowNextStep ? false : true,
      // Highlight the next incomplete step in this bot's shortcut list
      nextStepId: workflowNextStep?.id || null,
    },
    {
      id: 'money',
      name: 'Money Bot',
      description: moneyBotDescription,
      icon: CurrencyDollar,
      color: 'from-emerald-500 to-green-500',
      borderColor: 'border-emerald-500/30',
      bgColor: 'bg-emerald-500/10',
      // Round 218A Phase 8D-3D (2026-02-07) — Money Bot NEVER offers
      // "Create My Offer". The Offer is created ONCE inside Business
      // Builder → Offer Builder and lives in Business Brain forever.
      // Money Bot consumes that offer and builds assets from it.
      primaryAction: () => handleMoneyBotAccess(() => setShowSalesPageBuilder(true), 'sales_page'),
      primaryCta: 'Build Sales Page',
      primaryStepId: 'sales_page',
      secondaryAction: () => handleMoneyBotAccess(() => setShowLeadMagnetBuilder(true), 'lead_magnet'),
      secondaryCta: 'Lead Magnet',
      secondaryStepId: 'lead_magnet',
      tertiaryAction: () => handleMoneyBotAccess(() => setShowEmailSequenceBuilder(true), 'emails'),
      tertiaryCta: 'Emails',
      tertiaryStepId: 'emails',
      quaternaryAction: () => handleMoneyBotAccess(() => setShowBusinessEssentialsBuilder(true), 'essentials'),
      quaternaryCta: 'Essentials',
      quaternaryStepId: 'essentials',
      quinaryAction: () => handleMoneyBotAccess(() => setShowEbookBuilder(true), 'ebook'),
      quinaryCta: 'Ebook Builder',
      quinaryStepId: 'ebook',
      nextHint: brandDone ? 'Next: Drive traffic' : 'Tip: Brand Builder first for auto-branded assets',
      requiresApproval: !businessPlanApproved,
    },
    {
      id: 'content',
      name: 'Content Bot',
      description: 'Next: Drive traffic',
      icon: PenNib,
      color: 'from-purple-500 to-pink-500',
      borderColor: 'border-purple-500/30',
      bgColor: 'bg-purple-500/10',
      primaryAction: () => setShowGenerateModal(true),
      primaryCta: 'Generate Content',
      secondaryAction: () => setShowViralIdeasModal(true),
      secondaryCta: 'Find Viral Ideas',
      tertiaryAction: () => setShowContentPackModal(true),
      tertiaryCta: 'Content Pack',
      nextHint: 'Next: Scale your results',
    },
    {
      id: 'growth',
      name: 'Growth Bot',
      description: 'Next: Scale your results',
      icon: TrendUp,
      color: 'from-amber-500 to-orange-500',
      borderColor: 'border-amber-500/30',
      bgColor: 'bg-amber-500/10',
      primaryAction: () => setShowRunMyWeekModal(true),
      primaryCta: 'Plan My Week',
      // Run My Business lives here alongside Run My Week (minimal flow fix
      // Apr 2026) — both tools use the same active business memory.
      secondaryAction: () => setShowRunMyBusinessModal(true),
      secondaryCta: 'Run My Business',
      nextHint: '🔁 Repeat the loop',
    },
  ];

  return (
    <MissionHandoffProvider handoff={missionHandoff}>
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Test Mode Banner - Shows when plan is simulated */}
      
      <Navbar />
      
      {/* All Modals */}
      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} botName={selectedBot} />
      <InsufficientCreditsModal
        open={creditsModalOpen}
        onClose={() => setCreditsModalOpen(false)}
        title="Add Business Credits"
        message="Top up your Business Credits so professional production is ready when you need it. Your saved projects are safe."
      />
      <GenerateContentModal isOpen={showGenerateModal} onClose={() => { setShowGenerateModal(false); setSelectedViralIdea(null); showWin('Content created!'); fetchRecentContent(); }} initialTopic={initialTopic} selectedIdea={selectedViralIdea} onSuccess={(data) => { fetchRecentContent(); notifyBuilderCompleted(data); }} onAction={handleWhatsNextAction} onPickStep={pickMoneyBotStep} onFindViralIdeas={() => setShowViralIdeasModal(true)} onPlanMyWeek={() => setShowRunMyWeekModal(true)} />
      <RunMyWeekModal 
        isOpen={showRunMyWeekModal} 
        onClose={() => { setShowRunMyWeekModal(false); }} 
        onGenerateContent={handleGenerateClick} 
        onCreateVideos={() => { setShowRunMyWeekModal(false); setShowGenerateModal(true); }}
        user={user}
        onOpenWeeklyContentPlanner={() => { setShowRunMyWeekModal(false); setShowWeeklyContentPlanner(true); }}
        onSuccess={notifyBuilderCompleted}
      />
      <CloneViralModal isOpen={showCloneViralModal} onClose={() => setShowCloneViralModal(false)} onSuccess={fetchRecentContent} onAction={handleWhatsNextAction} />
      <BuildOfferModal isOpen={showBuildOfferModal} onClose={() => { setShowBuildOfferModal(false); setChatContextForOffer(null); showWin('Offer created!'); }} onAction={handleWhatsNextAction} onSuccess={() => { try { refreshWorkflowProgress?.(); } catch (_) { /* noop */ } }} chatContext={chatContextForOffer} />
      <EmailCampaignModal isOpen={showEmailCampaignModal} onClose={() => setShowEmailCampaignModal(false)} onAction={handleWhatsNextAction} />
      <SalesPageModal isOpen={showSalesPageModal} onClose={() => setShowSalesPageModal(false)} onAction={handleWhatsNextAction} />
      <MonetizationPlanModal 
        isOpen={showMonetizationPlanModal} 
        onClose={() => { setShowMonetizationPlanModal(false); showWin('Plan created!'); }} 
        onSuccess={() => { try { refreshWorkflowProgress?.(); } catch (_) { /* noop */ } }}
        onCreateContentPlan={() => { setShowMonetizationPlanModal(false); setShowRunMyWeekModal(true); }} 
        onBuildOffer={(planResult) => { 
          setShowMonetizationPlanModal(false); 
          // Pass plan context to Build Offer modal
          if (planResult) {
            const firstService = planResult.first_service || {};
            const bp = planResult.business_plan || {};
            setChatContextForOffer(JSON.stringify({
              niche: bp.niche || planResult.niche || '',
              audience: bp.target_audience || planResult.recommended_strategy || '',
              solution: firstService.description || planResult.recommended_strategy || '',
              offer_name: firstService.name || '',
              price: firstService.price || '',
            }));
          }
          setShowBuildOfferModal(true); 
        }}
      />
      <ViralIdeasModal isOpen={showViralIdeasModal} onClose={() => setShowViralIdeasModal(false)} onSelectIdea={handleViralIdeaSelect} onSelectIdeaForPack={handleViralIdeaSelectForPack} onSuccess={notifyBuilderCompleted} />
      <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
      <ImproveHookModal isOpen={showImproveHookModal} onClose={() => setShowImproveHookModal(false)} />
      <ScoreContentModal isOpen={showScoreContentModal} onClose={() => setShowScoreContentModal(false)} />
      <AnalyzeResultsModal isOpen={showAnalyzeResultsModal} onClose={() => setShowAnalyzeResultsModal(false)} onUseContent={(contentData) => { setShowAnalyzeResultsModal(false); setInitialTopic(contentData.content); setShowGenerateModal(true); }} />
      <ContentPackModal isOpen={showContentPackModal} onClose={() => { setShowContentPackModal(false); setSelectedViralIdea(null); showWin('Content pack created!'); }} onSuccess={(data) => { fetchRecentContent(); notifyBuilderCompleted(data); }} onPlanWeek={() => { setShowContentPackModal(false); setSelectedViralIdea(null); setShowRunMyWeekModal(true); }} selectedIdea={selectedViralIdea} />
      <LinkInBioModal isOpen={showLinkInBioModal} onClose={() => { setShowLinkInBioModal(false); showWin('Link setup created!'); }} onSuccess={() => {}} onCreateContent={() => { setShowLinkInBioModal(false); setShowGenerateModal(true); }} />
      <LeadMagnetBuilder isOpen={showLeadMagnetBuilder} onClose={() => { setShowLeadMagnetBuilder(false); fetchRecentContent(); }} onAction={handleWhatsNextAction} onPickStep={pickMoneyBotStep} onSuccess={notifyBuilderCompleted} user={user} />
      <SalesPageBuilder isOpen={showSalesPageBuilder} onClose={() => { setShowSalesPageBuilder(false); fetchRecentContent(); }} onAction={handleWhatsNextAction} onPickStep={pickMoneyBotStep} onSuccess={notifyBuilderCompleted} user={user} />
      <EbookBuilder isOpen={showEbookBuilder} onClose={() => { setShowEbookBuilder(false); fetchRecentContent(); }} onPickStep={pickMoneyBotStep} onSuccess={notifyBuilderCompleted} user={user} />
      <EmailSequenceBuilder isOpen={showEmailSequenceBuilder} onClose={() => { setShowEmailSequenceBuilder(false); fetchRecentContent(); }} onAction={handleWhatsNextAction} onPickStep={pickMoneyBotStep} onSuccess={notifyBuilderCompleted} user={user} />
      <BusinessEssentialsBuilder
        isOpen={showBusinessEssentialsBuilder}
        onClose={() => { setShowBusinessEssentialsBuilder(false); fetchRecentContent(); }}
        onNext={() => setShowEbookBuilder(true)}
        onPickStep={pickMoneyBotStep}
        onSuccess={notifyBuilderCompleted}
        user={user}
      />
      
      {/* Business Plan Review Modal - Blocks Money Bot until approved */}
      <BusinessPlanReviewModal 
        isOpen={showPlanReview} 
        onClose={() => setShowPlanReview(false)} 
        onApproved={handlePlanApproved} 
      />

      {/* Round 218B P0 (2026-02-07) — legacy OnboardingFocusModal
          retired. Business Builder Bot (MonetizationPlanModal) is
          the ONE AND ONLY first-time onboarding UI. */}

      {/* WIN MOMENT - Shows after completing an action */}
      <AnimatePresence>
        {showWinMoment && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-emerald-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center space-x-3"
          >
            <Confetti size={24} weight="fill" />
            <div>
              <p className="font-semibold">{winMessage}</p>
              <p className="text-sm text-emerald-100">Nice work — next step ready</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-white" data-testid="dashboard-title">Dashboard</h1>
            <div className="flex items-center space-x-2">
              <BusinessCreditsPill
                onAddCredits={() => setCreditsModalOpen(true)}
              />
              {billingInfo && <UpgradeButton onClick={handleUpgradeClick} variant="compact" currentPlan={billingInfo.plan_tier} />}
              {user?.daily_login_streak > 0 && <StreakBadge streak={user.daily_login_streak} />}
            </div>
          </div>
          {/* Round 37 — admin/founder only Active Business picker
              promoted to the top of the dashboard. Switching reloads
              the page so every bot reads the new active business. */}
          {(user?.is_internal || user?.plan_tier === 'internal') && (
            <div className="mt-3 flex items-center gap-3" data-testid="dashboard-header-business-picker">
              <ActiveBusinessPicker
                onSwitch={() => {
                  try { window.location.reload(); } catch { /* noop */ }
                }}
              />
              {/* Round 218A P0 (2026-02-07) — Founder Admin Reset.
                  Wipes ONLY the active business back to trial state
                  and relaunches onboarding. Never visible to
                  customers. */}
              <button
                onClick={async () => {
                  if (!window.confirm('Reset the active business back to a brand-new customer state? Onboarding will relaunch. Other businesses, subscriptions, credits, and saved assets are preserved.')) return;
                  try {
                    await api.post('/admin/reset-active-business');
                    toast.success('Active business reset — starting onboarding from Step 1.');
                    // Give the toast a moment, then hard-navigate to
                    // onboarding so every hook re-fetches fresh state.
                    // Business Builder Bot is the onboarding — the
                    // legacy /onboarding page has been retired. Land
                    // on the Dashboard and auto-open the plan modal.
                    setTimeout(() => { window.location.href = '/dashboard?openPlan=1'; }, 500);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Reset failed.');
                  }
                }}
                data-testid="founder-admin-reset-btn"
                className="text-xs font-semibold px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 transition-colors whitespace-nowrap"
                title="Founder-only. Wipes the active business back to trial state."
              >
                Admin Reset
              </button>
            </div>
          )}
        </div>

        {/* 1. TODAY'S FOCUS - Most dominant section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 bg-gradient-to-r ${todaysFocus.color} rounded-2xl p-5 sm:p-6 shadow-lg`}
          data-testid="todays-focus"
        >
          <div className="flex items-center space-x-2 mb-2">
            <Lightning size={18} className="text-yellow-300" weight="fill" />
            <span className="text-white/90 text-xs font-bold uppercase tracking-wider">{todaysFocus.botLabel}</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">{todaysFocus.title}</h2>
          <p className="text-white/80 text-sm mb-4">{todaysFocus.description}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              onClick={todaysFocus.action}
              className="flex-1 sm:flex-none px-6 py-3 bg-white text-slate-900 font-semibold rounded-xl hover:bg-slate-100 transition-all flex items-center justify-center space-x-2 shadow-md"
              data-testid="todays-focus-cta"
            >
              <FocusIcon size={20} weight="fill" />
              <span>{todaysFocus.cta}</span>
              <ArrowRight size={18} weight="bold" />
            </button>
            <span className="text-white/70 text-xs text-center sm:text-left">{todaysFocus.nextBot}</span>
          </div>
        </motion.div>

        {/* 1b. WHAT SHOULD I DO NEXT — the strategist's post-onboarding
             brief, always available on the Dashboard. Only shows for
             users who completed onboarding (i.e. have a wow-summary). */}
        {user?.onboarding_completed && (
          <WhatToDoNextCard />
        )}

        {/* 2. YOUR DAILY PLAN - For users with plans */}
        {user?.onboarding_completed && (
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <span className="text-white font-medium text-sm">Your Daily Plan</span>
            </div>
            <DailyMissionCard 
              onStartMission={() => setShowMonetizationPlanModal(true)}
              onPlanWeek={() => setShowRunMyWeekModal(true)}
              onCreateVideos={() => setShowGenerateModal(true)}
            />
          </div>
        )}

        {/* 2b. GETTING TO KNOW YOU — Round 90 Life Blueprint card
             (optional, dismissible, never blocks). */}
        <GettingToKnowYouCard />

        {/* 3. SYSTEM LOOP */}
        <div className="mb-6 py-3 px-4 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-center justify-center flex-wrap gap-1 sm:gap-2">
            <span className={`px-2 sm:px-3 py-1 ${currentStage === 'build' ? 'bg-blue-500 text-white' : 'bg-blue-500/20 text-blue-400'} text-xs font-medium rounded-lg`}>Build</span>
            <ArrowRight size={12} className="text-slate-500" />
            <span className={`px-2 sm:px-3 py-1 ${currentStage === 'monetize' ? 'bg-emerald-500 text-white' : 'bg-emerald-500/20 text-emerald-400'} text-xs font-medium rounded-lg`}>Monetize</span>
            <ArrowRight size={12} className="text-slate-500" />
            <span className={`px-2 sm:px-3 py-1 ${currentStage === 'create' ? 'bg-purple-500 text-white' : 'bg-purple-500/20 text-purple-400'} text-xs font-medium rounded-lg`}>Create</span>
            <ArrowRight size={12} className="text-slate-500" />
            <span className={`px-2 sm:px-3 py-1 ${currentStage === 'grow' ? 'bg-amber-500 text-white' : 'bg-amber-500/20 text-amber-400'} text-xs font-medium rounded-lg`}>Grow</span>
            <ArrowRight size={12} className="text-slate-500" />
            <span className="px-2 sm:px-3 py-1 bg-slate-500/20 text-slate-400 text-xs font-medium rounded-lg">🔁 Repeat</span>
          </div>
          <p className="text-slate-500 text-[10px] text-center mt-2">This is how your business grows. Keep going.</p>
        </div>

        {/* BUSINESS COMPLETION STATE - shows when offer + sales page + content exist */}
        <BusinessCompletionState 
          onPlanWeek={() => setShowRunMyWeekModal(true)}
          onViewCreations={() => navigate('/my-creations')}
        />

        {/* Round 218A Phase 8D-3C (2026-02-06) — Live Intelligence
            card. Polls /api/onboarding/intelligence/state and auto-
            refreshes as the background scheduler upgrades Market +
            Opportunity Intelligence from fallback → live. Never
            requires a full page reload. */}
        <LiveIntelligenceCard
          onLiveReady={() => {
            // Give the classic MI card a chance to pick up any
            // downstream persisted document updates. Best-effort —
            // works whether or not the classic pipeline is wired
            // to the same collection.
            try { refreshWorkflowProgress?.(); } catch (_e) { /* noop */ }
          }}
        />

        {/* Phase G.5 — Market Intelligence card (Round 210).
            Persistent strategic snapshot from Business Brain + Brand Brain + Market catalog.
            Sits above the 2x2 bot grid so every user sees the highest-ROI action first. */}
        <MarketIntelligenceCard />

        {/* 4. BOT CARDS - 2x2 Grid */}
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-3">
            {botSystem.map((bot) => {
              const Icon = bot.icon;
              return (
                <motion.div
                  key={bot.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`relative ${bot.bgColor} ${bot.borderColor} border rounded-xl p-3 sm:p-4`}
                  data-testid={`bot-${bot.id}`}
                >
                  {bot.isStart && (
                    <div className="absolute -top-1.5 -right-1.5">
                      <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-full" data-testid={`bot-${bot.id}-start-badge`}>START</span>
                    </div>
                  )}
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${bot.color} flex items-center justify-center mb-2`}>
                    <Icon size={16} className="text-white" weight="fill" />
                  </div>
                  <h3 className="text-white font-semibold text-xs sm:text-sm mb-0.5">{bot.name}</h3>
                  <p className="text-slate-400 text-[10px] sm:text-xs mb-2 leading-snug">{bot.description}</p>
                  
                  <div className="space-y-1.5">
                    <button
                      onClick={bot.primaryAction}
                      data-testid={bot.primaryStepId ? `workflow-btn-${bot.primaryStepId}` : undefined}
                      className={`w-full py-1.5 sm:py-2 bg-gradient-to-r ${bot.color} text-white text-[10px] sm:text-xs font-medium rounded-lg hover:opacity-90 transition-all flex items-center justify-center space-x-1 ${bot.primaryStepId && bot.nextStepId === bot.primaryStepId ? 'ring-2 ring-white/60' : ''}`}
                    >
                      <span>{bot.primaryCta}</span>
                      {bot.primaryStepId && isStepDone(bot.primaryStepId) ? (
                        <CheckCircle size={12} weight="fill" className="text-white/90" data-testid={`workflow-done-${bot.primaryStepId}`} />
                      ) : (
                        <ArrowRight size={10} weight="bold" />
                      )}
                    </button>
                    {bot.secondaryAction && (
                      <button
                        onClick={bot.secondaryAction}
                        data-testid={bot.secondaryStepId ? `workflow-btn-${bot.secondaryStepId}` : undefined}
                        className={`w-full py-1 sm:py-1.5 bg-white/10 hover:bg-white/20 text-slate-300 text-[9px] sm:text-[10px] font-medium rounded-md transition-all flex items-center justify-center space-x-1 ${bot.secondaryStepId && bot.nextStepId === bot.secondaryStepId ? 'ring-1 ring-white/50' : ''}`}
                      >
                        <span>{bot.secondaryCta}</span>
                        {bot.secondaryStepId && isStepDone(bot.secondaryStepId) && (
                          <CheckCircle size={10} weight="fill" className="text-emerald-300" data-testid={`workflow-done-${bot.secondaryStepId}`} />
                        )}
                      </button>
                    )}
                    {bot.tertiaryAction && (
                      <button
                        onClick={bot.tertiaryAction}
                        data-testid={bot.tertiaryStepId ? `workflow-btn-${bot.tertiaryStepId}` : undefined}
                        className={`w-full py-1 sm:py-1.5 bg-white/5 hover:bg-white/10 text-slate-400 text-[9px] sm:text-[10px] font-medium rounded-md transition-all flex items-center justify-center space-x-1 ${bot.tertiaryStepId && bot.nextStepId === bot.tertiaryStepId ? 'ring-1 ring-white/50' : ''}`}
                      >
                        <span>{bot.tertiaryCta}</span>
                        {bot.tertiaryStepId && isStepDone(bot.tertiaryStepId) && (
                          <CheckCircle size={10} weight="fill" className="text-emerald-300" data-testid={`workflow-done-${bot.tertiaryStepId}`} />
                        )}
                      </button>
                    )}
                    {bot.quaternaryAction && (
                      <button
                        onClick={bot.quaternaryAction}
                        data-testid={bot.quaternaryStepId ? `workflow-btn-${bot.quaternaryStepId}` : undefined}
                        className={`w-full py-1 sm:py-1.5 bg-white/5 hover:bg-white/10 text-slate-500 text-[9px] sm:text-[10px] font-medium rounded-md transition-all flex items-center justify-center space-x-1 ${bot.quaternaryStepId && bot.nextStepId === bot.quaternaryStepId ? 'ring-1 ring-white/50' : ''}`}
                      >
                        <span>{bot.quaternaryCta}</span>
                        {bot.quaternaryStepId && isStepDone(bot.quaternaryStepId) && (
                          <CheckCircle size={10} weight="fill" className="text-emerald-300" data-testid={`workflow-done-${bot.quaternaryStepId}`} />
                        )}
                      </button>
                    )}
                    {bot.quinaryAction && (
                      <button
                        onClick={bot.quinaryAction}
                        data-testid={bot.quinaryStepId ? `workflow-btn-${bot.quinaryStepId}` : undefined}
                        className={`w-full py-1 sm:py-1.5 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 hover:from-teal-500/30 hover:to-emerald-500/30 text-teal-400 text-[9px] sm:text-[10px] font-medium rounded-md transition-all border border-teal-500/30 flex items-center justify-center space-x-1 ${bot.quinaryStepId && bot.nextStepId === bot.quinaryStepId ? 'ring-1 ring-teal-300/70' : ''}`}
                      >
                        <span>{bot.quinaryCta}</span>
                        {bot.quinaryStepId && isStepDone(bot.quinaryStepId) && (
                          <CheckCircle size={10} weight="fill" className="text-emerald-300" data-testid={`workflow-done-${bot.quinaryStepId}`} />
                        )}
                      </button>
                    )}
                    {bot.senaryAction && (
                      <button
                        onClick={bot.senaryAction}
                        data-testid={bot.senaryStepId ? `workflow-btn-${bot.senaryStepId}` : "money-bot-ebook-builder-btn"}
                        className="w-full py-1 sm:py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 text-purple-300 text-[9px] sm:text-[10px] font-medium rounded-md transition-all border border-purple-500/30 flex items-center justify-center space-x-1"
                      >
                        <span>{bot.senaryCta}</span>
                        {bot.senaryStepId && isStepDone(bot.senaryStepId) && (
                          <CheckCircle size={10} weight="fill" className="text-emerald-300" data-testid={`workflow-done-${bot.senaryStepId}`} />
                        )}
                      </button>
                    )}
                  </div>
                  <p className="text-slate-500 text-[9px] mt-1.5 text-center" data-testid={`bot-${bot.id}-next-hint`}>{bot.nextHint}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* RUN MY BUSINESS - Week 1 Automation */}
        {user?.onboarding_completed && businessPlanApproved && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-gradient-to-br from-orange-500/10 via-pink-500/10 to-purple-500/10 border border-orange-500/30 rounded-xl"
            data-testid="run-my-business-section"
          >
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center">
                <Rocket size={16} className="text-white" weight="fill" />
              </div>
              <div>
                <span className="text-white font-semibold text-sm block">Run My Business — Week 1</span>
                <span className="text-orange-300 text-xs">Automated business launch system</span>
              </div>
            </div>
            <p className="text-slate-400 text-xs mb-3">
              Generate your complete business foundation, 14 Reels, 21 Stories, and a 7-day posting schedule — all ready for Metricool.
            </p>
            <button
              onClick={() => setShowRunMyBusinessModal(true)}
              data-testid="run-my-business-btn"
              className="w-full py-3 bg-gradient-to-r from-orange-500 via-pink-600 to-purple-600 hover:from-orange-600 hover:via-pink-700 hover:to-purple-700 text-white font-medium rounded-xl flex items-center justify-center space-x-2 transition-all shadow-lg shadow-orange-500/25"
            >
              <Rocket size={18} weight="fill" />
              <span>Launch Week 1</span>
            </button>
          </motion.div>
        )}

        {/* PRO EXCLUSIVE: Create Business Package */}
        {canGenerateBusinessPackage(user) && user?.onboarding_completed && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl"
            data-testid="pro-business-package-section"
          >
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                <Package size={16} className="text-white" weight="fill" />
              </div>
              <div>
                <span className="text-white font-semibold text-sm block">Pro Exclusive</span>
                <span className="text-purple-300 text-xs">Get your complete business package</span>
              </div>
              <span className="ml-auto px-2 py-0.5 bg-purple-500/30 text-purple-300 text-xs rounded-full">PRO</span>
            </div>
            <p className="text-slate-400 text-xs mb-3">
              Generate all your business assets in one click: Business Plan PDF, Sales Page, Lead Magnet, Email Sequence, Content Pack, and more — bundled in a ready-to-use ZIP file.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPackagePreview(true)}
                data-testid="preview-business-package-btn"
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
              >
                <Eye size={18} weight="bold" />
                <span>Preview Package</span>
              </button>
              <button
                onClick={handleGenerateBusinessPackage}
                disabled={generatingPackage}
                data-testid="generate-business-package-btn"
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
              >
                {generatingPackage ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkle size={18} weight="fill" />
                    <span>Generate</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {/* 5. INTERNAL TOOLS - Only for internal accounts */}
        {(user?.is_internal || user?.plan_tier === 'internal' || user?.simulation_active) && (
          <div className="mb-6 p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Lightning size={18} className="text-amber-400" weight="fill" />
                <span className="text-amber-400 font-semibold text-sm">Internal Tools</span>
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs rounded">Admin</span>
              </div>
              {/* Tier Simulator */}
            </div>
            <button
              onClick={() => setShowWeeklyContentPlanner(true)}
              data-testid="weekly-content-planner-btn"
              className="w-full py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
            >
              <Sparkle size={18} weight="fill" />
              <span>Weekly Content Planner</span>
              <span className="text-xs opacity-75">(21 posts)</span>
            </button>
            <p className="text-amber-200/60 text-xs mt-2 text-center">Generate content for Founder + PayGBot accounts</p>

            {/* Round 41 — Admin diagnostic + recovery tools */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="backfill-missing-weeks-btn"
                disabled={backfillRunning}
                onClick={async () => {
                  if (backfillRunning) return;
                  setBackfillRunning(true);
                  setBackfillResult(null);
                  try {
                    const res = await api.post('/admin/daily-content/backfill-missing', {});
                    setBackfillResult(res.data);
                    if (res.data?.success) {
                      toast.success(res.data.message || 'Backfill complete');
                    } else {
                      toast.error(res.data?.message || 'Backfill had failures');
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Backfill request failed');
                  } finally {
                    setBackfillRunning(false);
                  }
                }}
                className="py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium flex items-center justify-center gap-1.5"
              >
                {backfillRunning ? 'Backfilling…' : 'Backfill Missing Weeks'}
              </button>
              <button
                type="button"
                data-testid="scheduler-health-btn"
                disabled={schedulerHealthLoading}
                onClick={async () => {
                  if (schedulerHealthLoading) return;
                  setSchedulerHealthLoading(true);
                  setSchedulerHealth(null);
                  try {
                    const res = await api.get('/admin/scheduler/health');
                    setSchedulerHealth(res.data);
                    if (res.data?.healthy) {
                      toast.success(`All ${res.data.total_jobs} scheduler jobs alive`);
                    } else {
                      toast.error(`Dead jobs: ${(res.data?.dead_job_ids || []).join(', ') || 'unknown'}`);
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Health check failed');
                  } finally {
                    setSchedulerHealthLoading(false);
                  }
                }}
                className="py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium flex items-center justify-center gap-1.5"
              >
                {schedulerHealthLoading ? 'Checking…' : 'Check Scheduler Health'}
              </button>
            </div>

            {/* 2026-05-31 — Admin "Clean Broken Videos" — runs the
                ghost-row dry run, shows the count, asks for explicit
                confirmation, then applies the hard delete. Safe by
                construction: the backend only deletes rows with no
                recoverable asset (no objstore, no local file, no
                external CDN URL). */}
            <CleanBrokenVideosControl />

            {backfillResult && (
              <div
                data-testid="backfill-result"
                className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-slate-200 space-y-1"
              >
                <p className="font-semibold text-emerald-300">{backfillResult.message}</p>
                <p>
                  Range: {backfillResult.range?.from} → {backfillResult.range?.to}
                  {' · '}queued: <strong className="text-emerald-300">{backfillResult.total_queued}</strong>
                  {' · '}skipped: <strong>{backfillResult.total_skipped}</strong>
                  {' · '}failed: <strong className={backfillResult.total_failed ? 'text-rose-300' : ''}>{backfillResult.total_failed}</strong>
                </p>
                {(backfillResult.by_week || []).length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-emerald-300/80">Per-week breakdown</summary>
                    <ul className="mt-1 space-y-0.5 pl-4">
                      {backfillResult.by_week.map((w) => (
                        <li key={w.week_key}>
                          <span className="text-slate-400">{w.week_key}</span>{' '}
                          <span>{w.week_start} → {w.week_end}:</span>{' '}
                          queued={w.queued} · skipped={w.skipped} · failed={w.failed}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {(backfillResult.failures || []).length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-rose-300/80">{backfillResult.failures.length} failure(s)</summary>
                    <ul className="mt-1 space-y-0.5 pl-4">
                      {backfillResult.failures.map((f, i) => (
                        <li key={i} className="text-rose-200">{f.date}: {f.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {schedulerHealth && (
              <div
                data-testid="scheduler-health-result"
                className={`mt-3 rounded-lg border p-3 text-xs space-y-1 ${
                  schedulerHealth.healthy
                    ? 'border-emerald-500/30 bg-emerald-500/5 text-slate-200'
                    : 'border-rose-500/40 bg-rose-500/10 text-rose-100'
                }`}
              >
                <p className="font-semibold">
                  {schedulerHealth.healthy ? '✅ All jobs alive' : '⚠️ Some jobs are dead'}
                  {' '}({schedulerHealth.total_jobs} total)
                </p>
                <ul className="space-y-0.5">
                  {(schedulerHealth.jobs || []).map((j) => (
                    <li key={j.id} className="flex justify-between gap-2">
                      <span>
                        <span className={j.alive ? 'text-emerald-300' : 'text-rose-300'}>{j.alive ? '●' : '○'}</span>{' '}
                        <span className="font-mono text-[11px]">{j.id}</span>
                      </span>
                      <span className="text-slate-400">
                        {j.next_run_time ? new Date(j.next_run_time).toLocaleString() : 'NEVER (dead)'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Round 42 — Repair Calendar (self-healing gap detector) */}
            <div
              data-testid="repair-calendar-section"
              className="mt-4 pt-4 border-t border-amber-500/20"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-amber-300 font-semibold text-sm">Repair Calendar</span>
                  <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-200 text-[10px] rounded">self-healing</span>
                </div>
                <span className="text-[10px] text-slate-400">last 60d → today + 7d</span>
              </div>
              <p className="text-[11px] text-slate-400 mb-2">
                Scans <span className="font-mono">daily_content</span> for missing dates, failed jobs, incomplete generations, and Metricool sync gaps. Use dry-run first.
              </p>

              {/* Round 43 — Passive monitoring badge */}
              {lastScanInfo && (
                <div
                  data-testid="passive-monitoring-badge"
                  className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-600/40 bg-slate-800/40 p-2 text-[11px] text-slate-200"
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-slate-400">Auto-scan every 6h:</span>
                    {lastScanInfo.last_scan ? (
                      <>
                        <span data-testid="last-scan-timestamp">
                          last scan{' '}
                          <strong className="text-slate-100">
                            {new Date(lastScanInfo.last_scan.scanned_at).toLocaleString()}
                          </strong>
                        </span>
                        <span
                          data-testid="last-scan-needs-repair"
                          className={
                            (lastScanInfo.last_scan.needs_repair_count || 0) > 0
                              ? 'text-amber-300'
                              : 'text-emerald-300'
                          }
                        >
                          · needs repair:{' '}
                          <strong>{lastScanInfo.last_scan.needs_repair_count ?? 0}</strong>
                        </span>
                        {!lastScanInfo.last_scan.scheduler_healthy && (
                          <span className="text-rose-300">· scheduler unhealthy</span>
                        )}
                      </>
                    ) : (
                      <span data-testid="last-scan-none" className="text-slate-400">
                        no automated scan recorded yet (cron next runs at 6:15 UTC)
                      </span>
                    )}
                    {lastScanInfo.last_alert?.sent_at && (
                      <span
                        data-testid="last-alert-timestamp"
                        className="text-slate-400"
                      >
                        · last alert{' '}
                        <strong className="text-slate-200">
                          {new Date(lastScanInfo.last_alert.sent_at).toLocaleString()}
                        </strong>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-500">
                      email {lastScanInfo.alert_email_configured ? '✓' : '✗'} · slack{' '}
                      {lastScanInfo.slack_webhook_configured ? '✓' : '✗'} · cooldown{' '}
                      {lastScanInfo.cooldown_hours}h
                    </span>
                    <button
                      type="button"
                      data-testid="send-test-alert-btn"
                      disabled={alertNowLoading}
                      onClick={async () => {
                        if (alertNowLoading) return;
                        setAlertNowLoading(true);
                        try {
                          const res = await api.post('/admin/calendar/alert-now', { force: true });
                          if (res.data?.alerted) {
                            toast.success(`Test alert sent (reason: ${res.data.reason})`);
                          } else if (res.data?.reason === 'healthy') {
                            toast.success('Calendar is healthy — no alert needed');
                          } else {
                            toast.error(`Alert not sent: ${res.data?.reason || 'unknown'}`);
                          }
                          await refreshLastScanInfo();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Test alert failed');
                        } finally {
                          setAlertNowLoading(false);
                        }
                      }}
                      className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-[11px] text-white"
                    >
                      {alertNowLoading ? 'Sending…' : 'Send Test Alert'}
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  data-testid="calendar-scan-btn"
                  disabled={calendarScanLoading}
                  onClick={async () => {
                    if (calendarScanLoading) return;
                    setCalendarScanLoading(true);
                    setCalendarScan(null);
                    setRepairResult(null);
                    try {
                      const res = await api.get('/admin/calendar/scan');
                      setCalendarScan(res.data);
                      const c = res.data?.counts || {};
                      const totalNeeds = res.data?.needs_repair_count || 0;
                      if (totalNeeds === 0) {
                        toast.success('Calendar healthy — no gaps detected');
                      } else {
                        toast.message(`${totalNeeds} date(s) need repair (missing: ${c.missing || 0}, failed: ${c.failed || 0}, incomplete: ${c.incomplete || 0})`);
                      }
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Calendar scan failed');
                    } finally {
                      setCalendarScanLoading(false);
                    }
                  }}
                  className="py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium"
                >
                  {calendarScanLoading ? 'Scanning…' : 'Scan Calendar'}
                </button>
                <button
                  type="button"
                  data-testid="calendar-dry-run-btn"
                  disabled={repairLoading}
                  onClick={async () => {
                    if (repairLoading) return;
                    setRepairLoading(true);
                    setRepairResult(null);
                    try {
                      const res = await api.post('/admin/calendar/repair', { dry_run: true });
                      setRepairResult(res.data);
                      const planned = res.data?.planned_repairs ?? 0;
                      toast.message(`Dry-run: would repair ${planned} date(s)`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Dry-run failed');
                    } finally {
                      setRepairLoading(false);
                    }
                  }}
                  className="py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium"
                >
                  {repairLoading ? 'Working…' : 'Dry-Run Repair'}
                </button>
                <button
                  type="button"
                  data-testid="calendar-repair-btn"
                  disabled={repairLoading}
                  onClick={async () => {
                    if (repairLoading) return;
                    const plannedCount = (calendarScan?.needs_repair_count ?? null);
                    const promptText = plannedCount !== null
                      ? `Run repair for ${plannedCount} date(s)? This will queue real video/story jobs.`
                      : 'Run repair? This will queue real video/story jobs for every missing/failed/incomplete date.';
                    if (!window.confirm(promptText)) return;
                    setRepairLoading(true);
                    setRepairResult(null);
                    try {
                      const res = await api.post('/admin/calendar/repair', { dry_run: false, force: repairForce });
                      setRepairResult(res.data);
                      if (res.data?.blocked) {
                        toast.error(res.data.message || 'Repair blocked');
                      } else if (res.data?.success) {
                        toast.success(res.data.message || 'Repair complete');
                      } else {
                        toast.error(res.data?.message || 'Repair had failures');
                      }
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Repair request failed');
                    } finally {
                      setRepairLoading(false);
                    }
                  }}
                  className="py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium"
                >
                  {repairLoading ? 'Repairing…' : 'Run Repair'}
                </button>
              </div>
              <label className="flex items-center gap-2 mt-2 text-[11px] text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="calendar-repair-force-toggle"
                  checked={repairForce}
                  onChange={(e) => setRepairForce(e.target.checked)}
                  className="accent-rose-500"
                />
                <span>Force repair even when scheduler is unhealthy (use only after confirming the gap source)</span>
              </label>

              {calendarScan && (
                <div
                  data-testid="calendar-scan-result"
                  className="mt-3 rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-slate-200 space-y-2"
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-semibold text-blue-300">Scan {calendarScan.range?.from} → {calendarScan.range?.to}</span>
                    <span data-testid="calendar-scan-total" className="text-slate-300">total: <strong>{calendarScan.total_days}</strong></span>
                    <span data-testid="calendar-scan-needs-repair" className="text-amber-300">needs repair: <strong>{calendarScan.needs_repair_count}</strong></span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    <div data-testid="status-count-missing" className="px-2 py-1 rounded bg-rose-500/10 border border-rose-500/30 text-rose-200">
                      <span className="text-[10px] uppercase tracking-wide">Missing</span>{' '}
                      <strong className="float-right">{calendarScan.counts?.missing || 0}</strong>
                    </div>
                    <div data-testid="status-count-failed" className="px-2 py-1 rounded bg-orange-500/10 border border-orange-500/30 text-orange-200">
                      <span className="text-[10px] uppercase tracking-wide">Failed</span>{' '}
                      <strong className="float-right">{calendarScan.counts?.failed || 0}</strong>
                    </div>
                    <div data-testid="status-count-incomplete" className="px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
                      <span className="text-[10px] uppercase tracking-wide">Incomplete</span>{' '}
                      <strong className="float-right">{calendarScan.counts?.incomplete || 0}</strong>
                    </div>
                    <div data-testid="status-count-not-synced" className="px-2 py-1 rounded bg-yellow-500/10 border border-yellow-500/30 text-yellow-200">
                      <span className="text-[10px] uppercase tracking-wide">Not Synced</span>{' '}
                      <strong className="float-right">{calendarScan.counts?.not_synced || 0}</strong>
                    </div>
                    <div data-testid="status-count-synced" className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">
                      <span className="text-[10px] uppercase tracking-wide">Synced</span>{' '}
                      <strong className="float-right">{calendarScan.counts?.synced_to_metricool || 0}</strong>
                    </div>
                    <div data-testid="status-count-generated" className="px-2 py-1 rounded bg-slate-500/10 border border-slate-500/30 text-slate-200">
                      <span className="text-[10px] uppercase tracking-wide">Generated</span>{' '}
                      <strong className="float-right">{calendarScan.counts?.generated || 0}</strong>
                    </div>
                  </div>
                  {calendarScan.scheduler_health?.warning && (
                    <p data-testid="calendar-scan-scheduler-warning" className="text-rose-300 text-[11px] mt-1">
                      ⚠️ {calendarScan.scheduler_health.warning}
                    </p>
                  )}
                  <details className="mt-1">
                    <summary className="cursor-pointer text-blue-300/80">Per-day status ({calendarScan.days?.length || 0} dates)</summary>
                    <ul data-testid="calendar-scan-day-list" className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 pl-2 max-h-60 overflow-y-auto">
                      {(calendarScan.days || []).map((d) => {
                        const colorByStatus = {
                          missing: 'text-rose-300',
                          failed: 'text-orange-300',
                          incomplete: 'text-amber-300',
                          not_synced: 'text-yellow-300',
                          synced_to_metricool: 'text-emerald-300',
                          generated: 'text-slate-300',
                        };
                        return (
                          <li key={d.date} data-testid={`calendar-day-${d.date}`} className="flex justify-between font-mono text-[11px]">
                            <span className="text-slate-400">{d.date}</span>
                            <span className={colorByStatus[d.status] || 'text-slate-300'}>{d.status}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                </div>
              )}

              {repairResult && (
                <div
                  data-testid="calendar-repair-result"
                  className={`mt-3 rounded-lg border p-3 text-xs space-y-1 ${
                    repairResult.blocked
                      ? 'border-rose-500/40 bg-rose-500/10 text-rose-100'
                      : repairResult.dry_run
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
                      : repairResult.success
                      ? 'border-emerald-500/30 bg-emerald-500/5 text-slate-200'
                      : 'border-rose-500/40 bg-rose-500/10 text-rose-100'
                  }`}
                >
                  <p className="font-semibold">{repairResult.message}</p>
                  {repairResult.dry_run ? (
                    <>
                      <p data-testid="calendar-repair-planned-count">
                        Planned repairs: <strong className="text-amber-300">{repairResult.planned_repairs}</strong>
                        {' · '}range: {repairResult.range?.from} → {repairResult.range?.to}
                      </p>
                      {(repairResult.planned_dates || []).length > 0 && (
                        <details>
                          <summary className="cursor-pointer text-amber-300/80">Dates that would be repaired ({repairResult.planned_dates.length})</summary>
                          <ul data-testid="calendar-repair-planned-list" className="mt-1 pl-4 grid grid-cols-2 sm:grid-cols-3 gap-x-3 font-mono text-[11px]">
                            {repairResult.planned_dates.map((d, i) => (
                              <li key={`planned-${d}-${i}`}>{d}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </>
                  ) : (
                    <>
                      <p>
                        Repaired: <strong className="text-emerald-300" data-testid="calendar-repair-repaired-count">{repairResult.repaired_count ?? 0}</strong>
                        {' · '}failed: <strong className={repairResult.failed_count ? 'text-rose-300' : ''} data-testid="calendar-repair-failed-count">{repairResult.failed_count ?? 0}</strong>
                        {repairResult.metricool_updated && (
                          <span className="ml-2 text-emerald-300">· Metricool will refresh</span>
                        )}
                      </p>
                      {(repairResult.repaired || []).length > 0 && (
                        <details>
                          <summary className="cursor-pointer text-emerald-300/80">Repaired dates ({repairResult.repaired.length})</summary>
                          <ul data-testid="calendar-repair-success-list" className="mt-1 pl-4 grid grid-cols-1 sm:grid-cols-2 gap-x-3 font-mono text-[11px]">
                            {repairResult.repaired.map((r) => (
                              <li key={r.date}>
                                <span className="text-slate-400">{r.date}</span>{' '}
                                <span className="text-slate-500">({r.previous_status})</span>{' '}
                                <span className="text-emerald-300">→ {r.new_status}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {(repairResult.failed || []).length > 0 && (
                        <details>
                          <summary className="cursor-pointer text-rose-300/80">{repairResult.failed.length} failure(s)</summary>
                          <ul className="mt-1 pl-4 space-y-0.5">
                            {repairResult.failed.map((f, i) => (
                              <li key={i} className="text-rose-200 text-[11px]"><span className="font-mono">{f.date}</span>: {f.error}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {repairResult.scheduler_warning && (
                        <p data-testid="calendar-repair-scheduler-warning" className="text-rose-300 text-[11px] mt-1">
                          ⚠️ {repairResult.scheduler_warning}
                        </p>
                      )}
                      {repairResult.forced && (
                        <p className="text-amber-300 text-[11px]">⚠️ Repair was forced despite unhealthy scheduler.</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 6. ASK PAYGBOT - Support role */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-slate-400 text-xs">Stuck? Ask PayGBot what to do next</span>
          </div>
          <AskPayGBot 
            user={user}
            onToolAction={(action) => {
              if (action.type === 'open_tool') {
                switch (action.tool) {
                  case 'content_generation': setShowGenerateModal(true); break;
                  case 'viral_ideas': setShowViralIdeasModal(true); break;
                  case 'build_offer': setChatContextForOffer(action.chatContext || null); setShowBuildOfferModal(true); break;
                  case 'weekly_plan': setShowRunMyWeekModal(true); break;
                  case 'clone_viral': setShowCloneViralModal(true); break;
                  case 'monetization_plan': setShowMonetizationPlanModal(true); break;
                  case 'sales_page': setShowSalesPageModal(true); break;
                  case 'email_campaign': setShowEmailCampaignModal(true); break;
                  case 'improve_hook': setShowImproveHookModal(true); break;
                  case 'score_content': setShowScoreContentModal(true); break;
                  default: break;
                }
              } else if (action.type === 'navigate') {
                navigate(action.path);
              }
            }}
            defaultExpanded={false}
          />
        </div>

        {/* 6. ONBOARDING CHECKLIST - Fixed order */}
        <div className="mb-6">
          <OnboardingChecklist onAction={handleWhatsNextAction} />
        </div>

        {/* 7. SECONDARY SECTIONS - Collapsible */}
        <details className="mb-6 group">
          <summary className="flex items-center justify-between cursor-pointer list-none p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
            <span className="text-white font-medium text-sm">Progress & More</span>
            <span className="text-slate-400 text-xs group-open:hidden">Show →</span>
            <span className="text-slate-400 text-xs hidden group-open:inline">Hide</span>
          </summary>
          <div className="mt-3 space-y-4">
            {/* Progress intro line */}
            <p className="text-slate-400 text-xs">You're building momentum</p>
            {user?.onboarding_completed && <ProgressStats />}
            {billingInfo && <UsageMeters billingInfo={billingInfo} />}
            
            <div className="grid md:grid-cols-2 gap-4">
              {user?.onboarding_completed && <WeeklyReview onViewPlan={() => setShowRunMyWeekModal(true)} />}
              <ReferralCard />
            </div>

            {/* Recent Creations */}
            {recentContent.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium text-sm">Recent Creations</h3>
                  <div className="flex items-center gap-3">
                    {(user?.is_internal || user?.plan_tier === 'internal') && (
                      <button
                        onClick={() => navigate('/scheduler')}
                        className="text-emerald-400 hover:text-emerald-300 text-xs"
                        data-testid="dashboard-open-scheduler-link"
                      >
                        Metricool Scheduler →
                      </button>
                    )}
                    <button onClick={() => navigate('/my-creations')} className="text-blue-400 hover:text-blue-300 text-xs">View All</button>
                  </div>
                </div>
                <div className="space-y-2">
                  {recentContent.map((content, idx) => (
                    <div
                      key={`recent-${content.id || 'noid'}-${idx}`}
                      className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 transition-colors cursor-pointer"
                      onClick={() => navigate(`/content/${content.id}`)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          {content.video_prompt ? <VideoCamera size={14} className="text-white" /> : <PenNib size={14} className="text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white text-xs font-medium truncate">{content.title}</h4>
                          <p className="text-slate-400 text-[10px] truncate">{content.selected_hook}</p>
                        </div>
                        <CheckCircle size={14} weight="fill" className="text-emerald-400 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state for no content */}
            {recentContent.length === 0 && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                <VideoCamera size={32} className="text-slate-600 mx-auto mb-2" />
                <p className="text-slate-400 text-sm mb-3">No content yet</p>
                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  Create First Content
                </button>
              </div>
            )}
          </div>
        </details>
      </div>

      {/* Floating Feedback Button */}
      <button
        onClick={() => setShowFeedbackModal(true)}
        data-testid="feedback-button"
        className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-full shadow-lg flex items-center justify-center transition-all z-40"
      >
        <ChatCircle size={20} className="text-white" weight="fill" />
      </button>

      <AITwinSettingsModal isOpen={showAITwinModal} onClose={() => setShowAITwinModal(false)} onSuccess={() => setAITwinRefresh(prev => prev + 1)} />
      
      {/* Package Preview Modal for Pro users */}
      <PackagePreviewModal
        isOpen={showPackagePreview}
        onClose={() => setShowPackagePreview(false)}
        onGenerate={() => navigate('/my-creations')}
        user={user}
      />
      
      {/* Weekly Content Planner for Internal Accounts */}
      <WeeklyContentPlanner 
        isOpen={showWeeklyContentPlanner} 
        onClose={() => setShowWeeklyContentPlanner(false)} 
      />
      
      {/* Run My Business Modal */}
      <RunMyBusinessModal
        isOpen={showRunMyBusinessModal}
        onClose={() => setShowRunMyBusinessModal(false)}
        onSuccess={notifyBuilderCompleted}
        user={user}
      />

      {/* Round 218D P0 (2026-02-07) — canonical builder-completion
          continue dialog. Fires whenever ANY onboarding builder
          reports success. The button label + route come from
          /api/foundation/next-action so we have ONE source of truth
          for the entire onboarding chain. */}
      <Dialog open={continueDialogOpen} onOpenChange={setContinueDialogOpen}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white sm:max-w-md"
          data-testid="onboarding-continue-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-emerald-400" />
              {continueDialogMeta.title || 'Saved.'}
            </DialogTitle>
          </DialogHeader>
          {continueDialogMeta.subtitle && (
            <p className="text-slate-400 text-sm mt-1" data-testid="continue-dialog-subtitle">
              {continueDialogMeta.subtitle}
            </p>
          )}
          <div className="mt-4">
            <OnboardingContinueButton
              isOpen={continueDialogOpen}
              testid="continue-dialog-next-btn"
              onBeforeNavigate={() => {
                // Round 218D P0 (2026-02-07) — close ALL builder
                // modals BEFORE navigating so the resolver's next
                // destination is visible immediately (no stale
                // completed modal covering the new step).
                setContinueDialogOpen(false);
                setShowBuildOfferModal(false);
                setShowMonetizationPlanModal(false);
                setShowSalesPageBuilder(false);
                setShowLeadMagnetBuilder(false);
                setShowEmailSequenceBuilder(false);
                setShowBusinessEssentialsBuilder(false);
                setShowEbookBuilder(false);
                setShowViralIdeasModal(false);
                setShowGenerateModal(false);
                setShowContentPackModal(false);
                setShowRunMyWeekModal(false);
                setShowRunMyBusinessModal(false);
                setShowWeeklyContentPlanner(false);
              }}
            />
            <button
              type="button"
              onClick={() => setContinueDialogOpen(false)}
              data-testid="continue-dialog-dismiss"
              className="w-full mt-3 py-3 text-sm text-slate-400 hover:text-slate-200"
            >
              Stay here
            </button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Floating Help Button */}
      <FloatingHelpButton />
      
      {/* Package Ready Dialog */}
      <AnimatePresence>
        {showPackageDownload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowPackageDownload(false)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-slate-800 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              data-testid="package-download-dialog"
            >
              <div className="text-center mb-5">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={32} className="text-white" weight="fill" />
                </div>
                <h3 className="text-xl font-bold text-white">Package Ready!</h3>
                <p className="text-slate-400 text-sm mt-1">{packageFileCount} files generated</p>
              </div>

              {/* Primary: One-tap download */}
              <button
                onClick={handleDownloadPackage}
                data-testid="download-package-btn"
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl transition-all text-lg flex items-center justify-center space-x-2 mb-4"
              >
                <Package size={22} weight="fill" />
                <span>Download My Package</span>
              </button>

              {/* Secondary: Email delivery */}
              <details className="mb-3 group">
                <summary className="flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-300 text-sm transition-colors py-2">
                  <span>Or email the ZIP to yourself</span>
                </summary>
                <div className="mt-2 space-y-2">
                  <input
                    type="email"
                    value={packageEmail}
                    onChange={(e) => setPackageEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="package-email-input"
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    onClick={handleEmailPackage}
                    disabled={emailingPackage}
                    data-testid="email-package-btn"
                    className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium rounded-xl transition-all flex items-center justify-center space-x-2"
                  >
                    {emailingPackage ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <span>Send ZIP to Email</span>
                    )}
                  </button>
                </div>
              </details>

              {/* Tertiary: My Creations */}
              <button
                onClick={() => { setShowPackageDownload(false); navigate('/my-creations'); }}
                data-testid="view-creations-btn"
                className="w-full py-3 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl transition-colors text-sm"
              >
                View in My Creations
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </MissionHandoffProvider>
  );
};

export default DashboardPage;
